import { useEffect, useState } from 'react';
import { Alert, Button, Card, Empty, Select, Space, Table, Typography, message } from 'antd';
import { CloudSyncOutlined } from '@ant-design/icons';
import type {
  AlfaPeopleFieldKey,
  Dictionary,
  DictionarySyncConfig,
  DictionarySyncMode,
  DictionarySyncRun,
  DictionarySyncRunDetail,
} from '@lens/shared';
import { ALFA_PEOPLE_FIELDS } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { getDictionarySyncRun, saveDictionarySyncConfig } from './api.ts';

const { Text, Paragraph } = Typography;

interface Props {
  dictionary: Dictionary;
  config: DictionarySyncConfig | null;
  runs: DictionarySyncRun[];
  onSaved: (config: DictionarySyncConfig) => void;
  onRunSync: () => void;
}

interface Row {
  field: AlfaPeopleFieldKey;
  label: string;
  columnId: string | null;
  mode: DictionarySyncMode;
}

// Что разумно подставить новому справочнику: по slug колонок «Сотрудников ЕК».
const DEFAULT_SLUGS: Partial<Record<AlfaPeopleFieldKey, string[]>> = {
  fio: ['full_name', 'fio', 'displayname', 'name'],
  pernr: ['pernr', 'tabnum'],
  position: ['position'],
  employment: ['employment'],
  vendor: ['vendor'],
  team: ['team'],
  roleGuess: ['role'],
};
// ФИО и роль чаще правят руками — по умолчанию только при заведении.
const DEFAULT_CREATE_ONLY = new Set<AlfaPeopleFieldKey>(['fio', 'roleGuess']);

function rowsFrom(dictionary: Dictionary, config: DictionarySyncConfig | null): Row[] {
  return ALFA_PEOPLE_FIELDS.map((f) => {
    const m = config?.mapping.find((x) => x.field === f.key);
    if (config)
      return {
        field: f.key,
        label: f.label,
        columnId: m?.columnId ?? null,
        mode: m?.mode ?? 'update',
      };
    const guess = dictionary.columns.find(
      (c) =>
        c.id !== dictionary.keyColumnId &&
        (DEFAULT_SLUGS[f.key] ?? []).includes(c.slug.toLowerCase()),
    );
    return {
      field: f.key,
      label: f.label,
      columnId: guess?.id ?? null,
      mode: DEFAULT_CREATE_ONLY.has(f.key) ? 'create' : 'update',
    };
  });
}

/**
 * Вкладка «Alfa People» справочника: откуда брать людей, какое поле портала
 * в какую колонку, колонка «активен», история запусков.
 */
export function DictionarySyncTab({
  dictionary,
  config,
  runs,
  onSaved,
  onRunSync,
}: Props): JSX.Element {
  const [managers, setManagers] = useState<string[]>(config?.managers ?? []);
  const [rows, setRows] = useState<Row[]>(() => rowsFrom(dictionary, config));
  const [activeColumnId, setActiveColumnId] = useState<string | null>(
    config
      ? config.activeColumnId
      : (dictionary.columns.find((c) => c.slug === 'active' && c.columnType === 'boolean')?.id ??
          null),
  );
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState<Record<string, DictionarySyncRunDetail>>({});

  useEffect(() => {
    setManagers(config?.managers ?? []);
    setRows(rowsFrom(dictionary, config));
    if (config) setActiveColumnId(config.activeColumnId);
  }, [config, dictionary]);

  const keyColumn = dictionary.columns.find((c) => c.id === dictionary.keyColumnId);
  // Ключ всегда получает логин, колонку «активен» ведёт сама синхронизация.
  const columnOptions = dictionary.columns
    .filter((c) => c.id !== dictionary.keyColumnId && c.id !== activeColumnId)
    .map((c) => ({ value: c.id, label: c.name }));
  const booleanOptions = dictionary.columns
    .filter((c) => c.columnType === 'boolean')
    .map((c) => ({ value: c.id, label: c.name }));

  async function save() {
    setSaving(true);
    try {
      const saved = await saveDictionarySyncConfig(dictionary.id, {
        managers: managers.map((m) => m.trim()).filter(Boolean),
        activeColumnId,
        mapping: rows
          .filter((r) => r.columnId)
          .map((r) => ({ field: r.field, columnId: r.columnId!, mode: r.mode })),
      });
      onSaved(saved);
      message.success('Настройка сохранена');
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : 'Не удалось сохранить настройку');
    } finally {
      setSaving(false);
    }
  }

  async function loadDetail(runId: string) {
    if (details[runId]) return;
    try {
      const d = await getDictionarySyncRun(dictionary.id, runId);
      setDetails((prev) => ({ ...prev, [runId]: d }));
    } catch {
      message.error('Не удалось загрузить запуск');
    }
  }

  if (!keyColumn) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Сначала задайте ключевую колонку"
        description="Людей из портала ищем по логину в ключевой колонке справочника. Её задают на вкладке «Колонки»."
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="dict-sync-tab">
      <Card size="small" title="Откуда брать людей">
        <Paragraph type="secondary">
          Портал отдаёт подчинённых одного руководителя за запрос. Укажите табельные номера
          руководителей — людей соберём по всем. Логин из портала (без домена) ищем в колонке «
          {keyColumn.name}».
        </Paragraph>
        <Select
          mode="tags"
          style={{ width: '100%', maxWidth: 480 }}
          placeholder="Табельный номер руководителя, например 00255517"
          value={managers}
          onChange={(v: string[]) => setManagers(v)}
          tokenSeparators={[',', ' ']}
          open={false}
          data-testid="dict-sync-managers"
        />
      </Card>

      <Card size="small" title="Что куда писать">
        <Table<Row>
          size="small"
          rowKey="field"
          pagination={false}
          dataSource={rows}
          columns={[
            { title: 'Поле портала', dataIndex: 'label', width: 260 },
            {
              title: 'Колонка справочника',
              render: (_, r) => (
                <Select
                  allowClear
                  style={{ width: 240 }}
                  placeholder="Не писать"
                  value={r.columnId ?? undefined}
                  options={columnOptions}
                  onChange={(v: string | undefined) =>
                    setRows((prev) =>
                      prev.map((x) => (x.field === r.field ? { ...x, columnId: v ?? null } : x)),
                    )
                  }
                  data-testid={`dict-sync-col-${r.field}`}
                />
              ),
            },
            {
              title: 'Когда',
              width: 260,
              render: (_, r) => (
                <Select
                  style={{ width: 240 }}
                  disabled={!r.columnId}
                  value={r.mode}
                  options={[
                    { value: 'update', label: 'При каждом обновлении' },
                    { value: 'create', label: 'Только у новых' },
                  ]}
                  onChange={(v: DictionarySyncMode) =>
                    setRows((prev) =>
                      prev.map((x) => (x.field === r.field ? { ...x, mode: v } : x)),
                    )
                  }
                />
              ),
            },
          ]}
        />
        <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 4 }}>
          Пустое значение в портале ничего не стирает. Команды и другие ссылки на справочники ищутся
          по ключу или отображаемому значению записи.
        </Paragraph>
        <Space align="center" wrap>
          <Text>Колонка «активен» (снимаем у тех, кого нет в портале):</Text>
          <Select
            allowClear
            style={{ width: 240 }}
            placeholder="Не вести"
            value={activeColumnId ?? undefined}
            options={booleanOptions}
            onChange={(v: string | undefined) => setActiveColumnId(v ?? null)}
            data-testid="dict-sync-active"
          />
        </Space>
      </Card>

      <Space>
        <Button
          type="primary"
          loading={saving}
          onClick={() => void save()}
          data-testid="dict-sync-save"
        >
          Сохранить настройку
        </Button>
        <Button
          icon={<CloudSyncOutlined />}
          disabled={!config || config.managers.length === 0}
          onClick={onRunSync}
          data-testid="dict-sync-run"
        >
          Обновить из Alfa People
        </Button>
      </Space>

      <Card size="small" title="История обновлений">
        {runs.length === 0 ? (
          <Empty description="Ещё не обновляли" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table<DictionarySyncRun>
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={runs}
            expandable={{
              onExpand: (open, r) => {
                if (open) void loadDetail(r.id);
              },
              expandedRowRender: (r) => {
                const d = details[r.id];
                if (!d) return <Text type="secondary">Загрузка…</Text>;
                const lines = [
                  ...d.changes.create.map((c) => `Заведён: ${c.label}`),
                  ...d.changes.update.map(
                    (u) =>
                      `${u.label}: ${u.changes.map((c) => `${c.columnName} ${c.fromLabel} → ${c.toLabel}`).join('; ')}`,
                  ),
                  ...d.changes.deactivate.map((m) => `Снят с «активен»: ${m.label}`),
                ];
                return (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {lines.map((l, i) => (
                      <li key={i}>
                        <Text style={{ fontSize: 12 }}>{l}</Text>
                      </li>
                    ))}
                  </ul>
                );
              },
            }}
            columns={[
              {
                title: 'Когда',
                dataIndex: 'createdAt',
                render: (v: string) => new Date(v).toLocaleString('ru-RU'),
              },
              { title: 'Кто', render: (_, r) => r.user?.displayName ?? '—' },
              { title: 'В выгрузке', dataIndex: 'sourceCount' },
              { title: 'Заведено', dataIndex: 'created' },
              { title: 'Обновлено', dataIndex: 'updated' },
              { title: 'Снято', dataIndex: 'deactivated' },
            ]}
            data-testid="dict-sync-runs"
          />
        )}
      </Card>
    </Space>
  );
}
